import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { isSupabaseConfigured } from '@/lib/supabase'

// Layout
import Layout from '@/components/layout/Layout'

// Pages
import Login from '@/pages/Login'
import Register from '@/pages/Register'
import Dashboard from '@/pages/Dashboard'
import Logbook from '@/pages/Logbook'
import WorkoutSession from '@/pages/WorkoutSession'
import PEDPlanner from '@/pages/PEDPlanner'
import Settings from '@/pages/Settings'

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Auth route wrapper (redirects to home if already logged in)
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    )
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export default function App() {
  const { initialize: initAuth, user } = useAuthStore()
  const { loadProfile, createProfile } = useUserStore()

  // Initialize auth on mount
  useEffect(() => {
    initAuth()
  }, [initAuth])

  // Load or create user profile when authenticated
  useEffect(() => {
    if (user) {
      loadProfile(user.id).then(() => {
        // Check if profile exists, if not create one
        const { profile } = useUserStore.getState()
        if (!profile && user.email) {
          createProfile(user.id, user.email)
        }
      })
    }
  }, [user, loadProfile, createProfile])

  // Show config warning in development
  if (!isSupabaseConfigured()) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <div className="card max-w-md text-center">
          <h1 className="text-xl font-bold text-warning mb-4">Configuration Required</h1>
          <p className="text-text-secondary mb-4">
            Supabase is not configured. Please create a <code className="text-primary">.env.local</code> file with your Supabase credentials:
          </p>
          <pre className="bg-elevated rounded-lg p-4 text-left text-sm overflow-x-auto">
{`VITE_SUPABASE_URL=https://your-project.supabase.co
VITE_SUPABASE_ANON_KEY=your-anon-key`}
          </pre>
          <p className="text-text-muted text-sm mt-4">
            See the README for setup instructions.
          </p>
        </div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Auth routes */}
          <Route
            path="/login"
            element={
              <AuthRoute>
                <Login />
              </AuthRoute>
            }
          />
          <Route
            path="/register"
            element={
              <AuthRoute>
                <Register />
              </AuthRoute>
            }
          />

          {/* Protected app routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="logbook" element={<Logbook />} />
            <Route path="workout/:sessionId?" element={<WorkoutSession />} />
            <Route path="cycle" element={<PEDPlanner />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
